import type { Attendance, Employee, Shift } from "../api/types";

/**
 * One employee's line on the Check-in dashboard.
 *
 * The dashboard is a read-only join over data the People pillar already owns:
 * the directory supplies the people, the attendance register supplies today's
 * clock-in, and the roster supplies the shift they were assigned. Nothing here
 * writes, so it can never disagree with the modules it sits between.
 */
export type CheckInRow = {
  employee: Employee;
  attendance: Attendance | null;
  shift: Shift | null;
  checkedIn: boolean;
};

/** The most relevant attendance row for a day: one with a clock-in wins. */
function attendanceFor(
  rows: Attendance[],
  date: string,
): Map<string, Attendance> {
  const byEmployee = new Map<string, Attendance>();
  for (const row of rows) {
    if (row.work_date !== date) continue;
    const existing = byEmployee.get(row.employee);
    if (!existing || (!existing.clock_in_at && row.clock_in_at)) {
      byEmployee.set(row.employee, row);
    }
  }
  return byEmployee;
}

/** A working shift wins over an off day when a duplicate row exists. */
function shiftFor(rows: Shift[], date: string): Map<string, Shift> {
  const byEmployee = new Map<string, Shift>();
  for (const shift of rows) {
    if (shift.shift_date !== date) continue;
    if (shift.status === "cancelled") continue;
    const existing = byEmployee.get(shift.employee);
    if (!existing || (existing.is_off && !shift.is_off)) {
      byEmployee.set(shift.employee, shift);
    }
  }
  return byEmployee;
}

/**
 * Join employees, today's attendance and today's roster into one list.
 *
 * Exited employees are left out: they are history, not a check-in expectation.
 * A person with no attendance row is still present, with ``checkedIn: false``,
 * which is the whole point of the screen.
 */
export function buildCheckInRows(
  employees: Employee[],
  attendance: Attendance[],
  shifts: Shift[],
  date: string,
): CheckInRow[] {
  const attendanceByEmployee = attendanceFor(attendance, date);
  const shiftByEmployee = shiftFor(shifts, date);

  return employees
    .filter((employee) => employee.status !== "exited")
    .map((employee) => {
      const row = attendanceByEmployee.get(employee.id) ?? null;
      return {
        employee,
        attendance: row,
        shift: shiftByEmployee.get(employee.id) ?? null,
        checkedIn: Boolean(row?.clock_in_at),
      };
    });
}

function clock(value: string | null | undefined): string {
  return (value ?? "").slice(0, 5);
}

/** A compact shift label: name plus the rostered hours, never a blank cell. */
export function shiftSummary(shift: Shift | null): string {
  if (!shift) return "No shift assigned";
  if (shift.is_off) return shift.status === "draft" ? "Draft · Weekly off" : "Weekly off";
  const name = shift.template_name || "Shift";
  const start = clock(shift.start_time);
  const end = clock(shift.end_time);
  const summary = start && end ? `${name} · ${start}–${end}` : name;
  // A manager sees draft rosters; label them so the row does not imply the
  // employee is expected to check in against an unpublished shift.
  return shift.status === "draft" ? `Draft · ${summary}` : summary;
}
